import React, { useContext } from 'react';
import { WysiwygEditorStateContext } from '@src/contexts/wysiwyg/editor';
import { animated, SpringValue, to } from '@react-spring/web';

type TProps = {
  imageUrl: string;
  disableClip?: boolean;
  imageSpringValues: {
    x: SpringValue<number>;
    y: SpringValue<number>;
    height: SpringValue<number>;
    width: SpringValue<number>;
  };
  frameSpringValues: {
    x: SpringValue<number>;
    y: SpringValue<number>;
    height: SpringValue<number>;
    width: SpringValue<number>;
  };
  stackOrder: number;
};

function PossiblyWrapped({
  wrap,
  children
}: {
  wrap: boolean;
  children: React.ReactElement;
}) {
  const { renderRect } = useContext(WysiwygEditorStateContext);

  if (!wrap) return children;

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        height: renderRect.height,
        width: renderRect.width,
        overflow: 'hidden'
      }}
    >
      {children}
    </div>
  );
}

function ClippedPreviewImage({
  imageUrl,
  disableClip = false,
  imageSpringValues,
  frameSpringValues,
  stackOrder
}: TProps): React.ReactElement {
  const { renderRect, viewbox } = useContext(WysiwygEditorStateContext);

  const calculateTop = (fY: number, iY: number): number => {
    const frameY = (fY / viewbox.height) * renderRect.height;
    const imageY = (iY / viewbox.height) * renderRect.height;

    if (disableClip) {
      return frameY + imageY;
    } else {
      return frameY;
    }
  };
  const calculateLeft = (fX: number, iX: number): number => {
    const frameX = (fX / viewbox.width) * renderRect.width;
    const imageX = (iX / viewbox.width) * renderRect.width;

    if (disableClip) {
      return frameX + imageX;
    } else {
      return frameX;
    }
  };
  const calculateHeight = (fH: number, iH: number): number => {
    const frameHeight = (fH / viewbox.height) * renderRect.height;
    const imageHeight = (iH / viewbox.height) * renderRect.height;

    return disableClip ? imageHeight : frameHeight;
  };
  const calculateWidth = (fW: number, iW: number): number => {
    const frameWidth = (fW / viewbox.width) * renderRect.width;
    const imageWidth = (iW / viewbox.width) * renderRect.width;

    return disableClip ? imageWidth : frameWidth;
  };

  if (!renderRect) return null;

  return (
    <PossiblyWrapped wrap={disableClip}>
      <animated.div
        className="wysiwyg-editor-builder-imagecontainer"
        style={{
          top: to([frameSpringValues.y, imageSpringValues.y], calculateTop),
          left: to([frameSpringValues.x, imageSpringValues.x], calculateLeft),
          height: to(
            [frameSpringValues.height, imageSpringValues.height],
            calculateHeight
          ),
          width: to(
            [frameSpringValues.width, imageSpringValues.width],
            calculateWidth
          ),
          backgroundImage: `url(${imageUrl})`,
          backgroundPosition: to(
            [imageSpringValues.x, imageSpringValues.y],
            (x, y) => {
              if (disableClip) return 'top left';

              const top = (y / viewbox.height) * renderRect.height;
              const left = (x / viewbox.width) * renderRect.width;

              return `${left}px  ${top}px`;
            }
          ),
          backgroundSize: to(
            [imageSpringValues.height, imageSpringValues.width],
            (h, w) => {
              const height = (h / viewbox.height) * renderRect.height;
              const width = (w / viewbox.width) * renderRect.width;

              return `${width}px ${height}px`;
            }
          ),
          overflow: disableClip ? 'visible' : 'hidden',
          position: 'absolute',
          zIndex: stackOrder
        }}
      />
    </PossiblyWrapped>
  );
}

export default ClippedPreviewImage;
